import React, { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useActiveTenantId } from '@/store/tenantStore'
import {
  Badge,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Textarea,
  useToast,
} from '@biartbt/biartd'
import { CrudPage } from '@/components/Crud'
import type { Position } from '../api/positionsApi'
import { positionsApi } from '../api/positionsApi'
import { departmentsApi } from '@/modules/organization/departments/api/departmentsApi'
import type { Department } from '@/modules/organization/departments/api/departmentsApi'

type PositionForm = {
  code: string
  name: string
  description: string
  departmentId: string
  workModel: string
  jobDefinition: string
  responsibilities: string
  trainingRequirements: string
  experienceLevel: string
  certifications: string
  level: string
  workingHours: string
  isActive: boolean
}

function emptyToNull(s: string): string | null {
  const t = s?.trim()
  return !t ? null : t
}

const WORK_MODEL_OPTIONS = [
  { value: 'office', label: 'positions.workModel.office' as const },
  { value: 'hybrid', label: 'positions.workModel.hybrid' as const },
  { value: 'remote', label: 'positions.workModel.remote' as const },
]

const EXPERIENCE_LEVEL_OPTIONS = [
  { value: 'entry', label: 'positions.experienceLevel.entry' as const },
  { value: 'mid', label: 'positions.experienceLevel.mid' as const },
  { value: 'senior', label: 'positions.experienceLevel.senior' as const },
  { value: 'manager', label: 'positions.experienceLevel.manager' as const },
]

export default function PositionsPage() {
  const [searchParams] = useSearchParams()
  const activeTenantId = useActiveTenantId()
  const tenantIdFromUrl = searchParams.get('tenantId')
  const tenantId = tenantIdFromUrl ? Number(tenantIdFromUrl) : activeTenantId
  const { toast } = useToast()
  const { t } = useTranslation()

  const [departments, setDepartments] = useState<Department[]>([])
  const [lookupsLoading, setLookupsLoading] = useState(false)

  const loadLookups = useCallback(async () => {
    if (!tenantId) return
    setLookupsLoading(true)
    try {
      const deptRes = await departmentsApi.list({ tenantId, pageSize: 500, isActive: true })
      setDepartments(deptRes.items)
    } catch {
      setDepartments([])
    } finally {
      setLookupsLoading(false)
    }
  }, [tenantId])

  useEffect(() => {
    loadLookups()
  }, [loadLookups])

  const deptOptions = departments.map((d) => ({
    value: String(d.departmentId),
    label: `${d.code ?? ''} - ${d.name}`.trim() || d.name,
  }))

  const workModelLabel = (value: string | null | undefined) => {
    if (!value) return '-'
    const opt = WORK_MODEL_OPTIONS.find((o) => o.value === value)
    return opt ? t(opt.label) : value
  }

  const expLevelLabel = (value: string | null | undefined) => {
    if (!value) return '-'
    const opt = EXPERIENCE_LEVEL_OPTIONS.find((o) => o.value === value)
    return opt ? t(opt.label) : value
  }

  return (
    <CrudPage<Position, PositionForm, any, any>
      title={t('positions.title')}
      subtitle={t('positions.subtitle')}
      primaryActionLabel={t('positions.newPosition')}
      onExportClick={() =>
        toast({
          title: t('common.notImplemented'),
          description: t('positions.exportNotImplemented'),
          variant: 'warning',
        })
      }
      onImportClick={() =>
        toast({
          title: t('common.notImplemented'),
          description: t('positions.importNotImplemented'),
          variant: 'warning',
        })
      }
      list={async (p) =>
        positionsApi.list({
          tenantId,
          search: p.search,
          page: p.page,
          pageSize: p.pageSize,
          sortBy: (p.sortBy as any) ?? undefined,
          sortDir: (p.sortDir as any) ?? undefined,
          isActive: p.filters.status === 'all' ? null : p.filters.status === 'active',
          includeDeleted: p.filters.deleted === 'show',
        })
      }
      getRowId={(pos) => pos.positionId}
      columns={[
        {
          key: 'code',
          header: t('positions.columns.code'),
          sortable: true,
          widthClassName: 'w-[120px]',
          render: (pos) => <span className="font-medium">{pos.code}</span>,
        },
        {
          key: 'name',
          header: t('positions.columns.name'),
          sortable: true,
          widthClassName: 'w-[220px]',
          render: (pos) => pos.name,
        },
        {
          key: 'departmentId',
          header: t('positions.columns.department'),
          sortable: false,
          widthClassName: 'w-[180px]',
          render: (pos) => {
            if (!pos.departmentId) return '-'
            const dept = departments.find((d) => d.departmentId === pos.departmentId)
            return dept?.name ?? String(pos.departmentId)
          },
        },
        {
          key: 'workModel',
          header: t('positions.columns.workModel'),
          sortable: true,
          widthClassName: 'w-[140px]',
          render: (pos) => workModelLabel(pos.workModel),
        },
        {
          key: 'experienceLevel',
          header: t('positions.columns.experienceLevel'),
          sortable: false,
          widthClassName: 'w-[140px]',
          render: (pos) => expLevelLabel(pos.experienceLevel),
        },
        {
          key: 'isActive',
          header: t('positions.columns.status'),
          sortable: false,
          widthClassName: 'w-[100px]',
          render: (pos) =>
            pos.isActive ? (
              <Badge variant="success">{t('positions.status.active')}</Badge>
            ) : (
              <Badge variant="warning">{t('positions.status.inactive')}</Badge>
            ),
        },
      ]}
      searchPlaceholder={t('positions.searchPlaceholder')}
      filters={[
        {
          id: 'status',
          label: t('positions.filters.status'),
          value: 'all',
          options: [
            { label: t('positions.filters.all'), value: 'all' },
            { label: t('positions.status.active'), value: 'active' },
            { label: t('positions.status.inactive'), value: 'inactive' },
          ],
        },
        {
          id: 'deleted',
          label: t('positions.filters.deleted'),
          value: 'hide',
          options: [
            { label: t('positions.filters.hideDeleted'), value: 'hide' },
            { label: t('positions.filters.showDeleted'), value: 'show' },
          ],
          widthClassName: 'min-w-[160px]',
        },
      ]}
      initialPageSize={10}
      modalTitles={{
        create: t('positions.modal.create'),
        edit: t('positions.modal.edit'),
      }}
      getCreateDefaultForm={() => ({
        code: '',
        name: '',
        description: '',
        departmentId: '',
        workModel: '',
        jobDefinition: '',
        responsibilities: '',
        trainingRequirements: '',
        experienceLevel: '',
        certifications: '',
        level: '',
        workingHours: '',
        isActive: true,
      })}
      getEditForm={(pos) => ({
        code: pos.code ?? '',
        name: pos.name ?? '',
        description: pos.description ?? '',
        departmentId: pos.departmentId != null ? String(pos.departmentId) : '',
        workModel: pos.workModel ?? '',
        jobDefinition: pos.jobDefinition ?? '',
        responsibilities: pos.responsibilities ?? '',
        trainingRequirements: pos.trainingRequirements ?? '',
        experienceLevel: pos.experienceLevel ?? '',
        certifications: pos.certifications ?? '',
        level: pos.level ?? '',
        workingHours: pos.workingHours ?? '',
        isActive: pos.isActive,
      })}
      validate={(f) => ({
        code: !f.code.trim()
          ? t('positions.validation.codeRequired')
          : f.code.trim().length > 100
            ? t('positions.validation.codeMaxLength')
            : undefined,
        name: !f.name.trim()
          ? t('positions.validation.nameRequired')
          : f.name.trim().length > 255
            ? t('positions.validation.nameMaxLength')
            : undefined,
      })}
      renderForm={({ form, setForm, touched, setTouched, errors, isSubmitting }) => (
        <div className="grid gap-4">
          <div className="grid grid-cols-2 gap-4">
            <Input
              label={t('positions.fields.code')}
              value={form.code}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setForm((s) => ({ ...s, code: e.target.value }))
              }
              onBlur={() => setTouched(true)}
              error={touched && !!errors.code}
              helperText={touched ? errors.code : undefined}
              required
              disabled={isSubmitting}
              placeholder={t('positions.fields.codePlaceholder')}
              leftIcon={<i className="material-symbols-outlined text-lg">tag</i>}
            />
            <Input
              label={t('positions.fields.name')}
              value={form.name}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setForm((s) => ({ ...s, name: e.target.value }))
              }
              onBlur={() => setTouched(true)}
              error={touched && !!errors.name}
              helperText={touched ? errors.name : undefined}
              required
              disabled={isSubmitting}
              placeholder={t('positions.fields.namePlaceholder')}
              leftIcon={<i className="material-symbols-outlined text-lg">work</i>}
            />
          </div>

          <Textarea
            label={t('positions.fields.description')}
            value={form.description}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
              setForm((s) => ({ ...s, description: e.target.value }))
            }
            disabled={isSubmitting}
            placeholder={t('positions.fields.descriptionPlaceholder')}
          />

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1.5">
                {t('positions.fields.department')}
              </label>
              <Select
                value={form.departmentId ? String(form.departmentId) : '__none__'}
                onValueChange={(v: string) =>
                  setForm((s) => ({ ...s, departmentId: v === '__none__' ? '' : v }))
                }
                disabled={isSubmitting || lookupsLoading}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('positions.fields.departmentPlaceholder')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">{t('common.none')}</SelectItem>
                  {deptOptions.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1.5">
                {t('positions.fields.workModel')}
              </label>
              <Select
                value={form.workModel || '__none__'}
                onValueChange={(v: string) =>
                  setForm((s) => ({ ...s, workModel: v === '__none__' ? '' : v }))
                }
                disabled={isSubmitting}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('positions.fields.workModelPlaceholder')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">{t('common.none')}</SelectItem>
                  {WORK_MODEL_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {t(o.label)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1.5">
                {t('positions.fields.experienceLevel')}
              </label>
              <Select
                value={form.experienceLevel || '__none__'}
                onValueChange={(v: string) =>
                  setForm((s) => ({ ...s, experienceLevel: v === '__none__' ? '' : v }))
                }
                disabled={isSubmitting}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('positions.fields.experienceLevelPlaceholder')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">{t('common.none')}</SelectItem>
                  {EXPERIENCE_LEVEL_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {t(o.label)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Input
              label={t('positions.fields.level')}
              value={form.level}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setForm((s) => ({ ...s, level: e.target.value }))
              }
              disabled={isSubmitting}
              placeholder={t('positions.fields.levelPlaceholder')}
            />
          </div>

          <Input
            label={t('positions.fields.workingHours')}
            value={form.workingHours}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setForm((s) => ({ ...s, workingHours: e.target.value }))
            }
            disabled={isSubmitting}
            placeholder={t('positions.fields.workingHoursPlaceholder')}
          />

          <Textarea
            label={t('positions.fields.jobDefinition')}
            value={form.jobDefinition}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
              setForm((s) => ({ ...s, jobDefinition: e.target.value }))
            }
            disabled={isSubmitting}
            placeholder={t('positions.fields.jobDefinitionPlaceholder')}
          />

          <Textarea
            label={t('positions.fields.responsibilities')}
            value={form.responsibilities}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
              setForm((s) => ({ ...s, responsibilities: e.target.value }))
            }
            disabled={isSubmitting}
            placeholder={t('positions.fields.responsibilitiesPlaceholder')}
          />

          <Textarea
            label={t('positions.fields.trainingRequirements')}
            value={form.trainingRequirements}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
              setForm((s) => ({ ...s, trainingRequirements: e.target.value }))
            }
            disabled={isSubmitting}
            placeholder={t('positions.fields.trainingRequirementsPlaceholder')}
          />

          <Textarea
            label={t('positions.fields.certifications')}
            value={form.certifications}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
              setForm((s) => ({ ...s, certifications: e.target.value }))
            }
            disabled={isSubmitting}
            placeholder={t('positions.fields.certificationsPlaceholder')}
          />

          <Switch
            checked={form.isActive}
            onCheckedChange={(v: boolean) => setForm((s) => ({ ...s, isActive: v }))}
            label={t('positions.fields.isActive')}
            disabled={isSubmitting}
          />
        </div>
      )}
      buildCreatePayload={(f) => ({
        tenantId,
        code: f.code.trim(),
        name: f.name.trim(),
        description: emptyToNull(f.description),
        departmentId: f.departmentId ? Number(f.departmentId) : null,
        workModel: emptyToNull(f.workModel),
        jobDefinition: emptyToNull(f.jobDefinition),
        responsibilities: emptyToNull(f.responsibilities),
        trainingRequirements: emptyToNull(f.trainingRequirements),
        experienceLevel: emptyToNull(f.experienceLevel),
        certifications: emptyToNull(f.certifications),
        level: emptyToNull(f.level),
        workingHours: emptyToNull(f.workingHours),
        isActive: f.isActive,
      })}
      buildUpdatePayload={(f, item) => ({
        tenantId,
        code: f.code.trim(),
        name: f.name.trim(),
        description: emptyToNull(f.description),
        departmentId: f.departmentId ? Number(f.departmentId) : null,
        workModel: emptyToNull(f.workModel),
        jobDefinition: emptyToNull(f.jobDefinition),
        responsibilities: emptyToNull(f.responsibilities),
        trainingRequirements: emptyToNull(f.trainingRequirements),
        experienceLevel: emptyToNull(f.experienceLevel),
        certifications: emptyToNull(f.certifications),
        level: emptyToNull(f.level),
        workingHours: emptyToNull(f.workingHours),
        isActive: f.isActive,
      })}
      create={(payload) => positionsApi.create(payload)}
      update={(id, payload) => positionsApi.update({ id: Number(id), ...(payload as any) })}
      remove={(id) => positionsApi.delete(Number(id))}
      getCreatedId={(res) => res?.data?.id}
    />
  )
}
